import React, {
  Component,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import SashiDo from 'lib/SashiDo';
import GoogleTagManager from 'lib/GoogleTagManager';

import Icon from 'components/Icon/Icon.react';
import Toggle from 'components/Toggle/Toggle.react';
import Modal from 'components/Modal/Modal.react';
import { Link } from 'react-router';
import styles from './Login.scss';
import stylesSettings from 'dashboard/Settings/Settings.scss';
import Notification from 'components/Notification/Notification.react';
import validateEmailFormat from 'lib/validateEmailFormat';

import Label from 'components/Label/Label.react';
import { createWidget } from '@typeform/embed';

const FORM_ID = 'U93yw07D';

const getEmailFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('email') || '';
};

export const Registration = () => {
  const refEmail = useRef(null);
  const typeformContainer = useRef(null);

  const [step, setStep] = useState(0);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [email, setEmail] = useState(getEmailFromURL());

  useEffect(() => {
    const emailFromURL = getEmailFromURL();
    const isValid = validateEmailFormat(emailFromURL.toLowerCase().replace(/ /g, ''));
    if (isValid) {
      setEmail(emailFromURL);
      setStep(2);
    }
  }, []);

  const showTypeform = useCallback(() => {
    if (typeformContainer.current) {
      const typeform = createWidget(FORM_ID, {
        container: typeformContainer.current,
        onSubmit: () => {
          if (!email) {
            console.error('No email available for redirect');
            return;
          }

          window.location.href = `/register?email=${encodeURIComponent(email)}`;
        },
        hideHeaders: true,
        hideFooter: true,
      });

      return () => typeform.unload();
    }
  }, [email]);

  const handleSubmitButton = useCallback((e) => {
    e.preventDefault();
    const emailInput = refEmail.current.value;
    const cleanEmail = emailInput.toLowerCase().replace(/ /g, '');

    if (!validateEmailFormat(cleanEmail)) {
      setIsButtonEnabled(false);
      return;
    }

    setEmail(cleanEmail);
    setIsButtonEnabled(true);
    setStep(1);

    setTimeout(() => {
      showTypeform();
    }, 40);
  }, [showTypeform]);

  const handleOnChange = useCallback((e) => {
    e.preventDefault();
    const emailInput = refEmail.current.value;
    const cleanEmail = emailInput.toLowerCase().replace(/ /g, '');
    const isValid = validateEmailFormat(cleanEmail);

    setIsButtonEnabled(isValid);
    if (isValid) {
      setEmail(cleanEmail);
    }
  }, []);

  if (step === 0) {
    return (
      <>
        <div
          className={[styles.login, styles.isActive, styles.customTypeform].join(' ')}
        >
          <div>
            <div className={styles.showEffect} />
            <div className={styles.wrapper}>
              <Link to="/register" className={styles.logo}>
                <img src="/assets/images/logo.svg" width="180" alt="SashiDo" />
              </Link>
              <div className={styles.content}>
                <form className={styles.form} onSubmit={handleSubmitButton}>
                  <p className={styles.title}>Get Started</p>

                  <Label text="* Email" />
                  <div className={styles.inputHolder}>
                    <input
                      ref={refEmail}
                      name="username"
                      className={styles.username}
                      type="email"
                      defaultValue={email}
                      onChange={handleOnChange}
                    />
                    <div className={styles.inputIconHolder}>
                      <Icon name="icon-envelope" width={16} height={16} fill="#fff" />
                    </div>
                  </div>

                  <a
                    id="submit"
                    href="javascript:;"
                    className={[styles.button, (!isButtonEnabled) ? styles.disable : ''].join(' ')}
                    onClick={handleSubmitButton}>
                    <i className={styles.spinner} /><span>Get Started</span>
                  </a>
                </form>
              </div>
              <a href="/login" className={styles.signUp}>Already have an account? Log In</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (step === 1) {
    return (
      <div
        className={[styles.login, styles.isActive, styles.customTypeform].join(' ')}
      >
        <div className={styles.typeform} ref={typeformContainer} />
      </div>
    );
  }

  if (step === 2) {
    return (
      <RegistrationForm email={email} />
    );
  }

  return null;
};

export default Registration;

class RegistrationForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isActive: false,
      note: {
        type: '',
        message: '',
      },
      agree: false,
      showPolicies: false,
      showTos: false,
      user: null,
    };

    this.handleKey = this.handleKey.bind(this);
    this.handleSubmitButton = this.handleSubmitButton.bind(this);
  }

  componentDidMount() {
    SashiDo.logOut();
    document.body.addEventListener('keydown', this.handleKey);
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleKey);
  }

  onClose() {
    this.setState({
      agree: false,
      showPolicies: false,
      showTos: false,
    });
  }

  handleKey(e) {
    const keyCode = e.keyCode || e.which;
    switch (keyCode) {
      case 13:
        this.handleSubmitButton(this);
        break;
    }
  }

  handleSubmitButton() {
    const email = this.props.email || this.emailInput;

    this.setState({ isActive: true, note: null });

    const referral_code = this.props.params ? this.props.params.referral_code : undefined;
      SashiDo.userRegistration({
        username: email,
        honeypot: email,
        referral_code,
        from_telerik_platform_migration: false,
        agreeAccountCreating: this.state.agree,
        agreeReceiveNewsletter: this.state.agreeMail,
      }).then(() => {
          this.setState({
            isActive: false,
            registrationDone: true,
          });

        GoogleTagManager({
          data: {
            event: 'registered',
            pageCategory: 'sign-up',
          },
        });
      }).catch((error) => {
          if (error.response) {
            this.setState({
              isActive: false,
              note: {
                type: 'error',
                message: error.response.data.error,
              },
            });
          }
      });
  }

  render() {
    const emailFromTypeform = this.props.email;
    const classes = [
      styles.content,
    ];

    if (this.state.isActive) {
      classes.push(styles.loading);
    }

    const policiesView =
      this.state.showPolicies &&
      <Modal
        width={740}
        title="Privacy Notice"
        cancelText="Go Back"
        onCancel={() => this.onClose()}>
        <div>
          <iframe className={stylesSettings.Policies} width="100%" height="100%" src="https://www.sashido.io/en/policies/dashboard#gdpr-privacy-notice" />
          <div className={stylesSettings.modalCustomInfo}>
            <p>You represent and warrant that:</p>

            <ol>
              <li>You have full legal authority to agree to these terms presented above.</li>
              <li>You have read and understand these terms.</li>
              <li>You agree to these terms.</li>
            </ol>

            <p>If you do not have the authority, or do not agree to these terms, do not click the "I Accept" button below.</p>
          </div>
        </div>
      </Modal>;

    const tosView =
      this.state.showTos &&
      <Modal
        width={740}
        title="Terms of Service"
        cancelText="Go Back"
        onCancel={() => this.onClose()}>
        <div>
          <iframe className={stylesSettings.Policies} width="100%" height="100%" src="https://www.sashido.io/en/policies/dashboard" />
          <div className={stylesSettings.modalCustomInfo}>
            <p>You represent and warrant that:</p>

            <ol>
              <li>You have full legal authority to agree to these terms presented above.</li>
              <li>You have read and understand these terms.</li>
              <li>You agree to these terms.</li>
            </ol>

            <p>If you do not have the authority, or do not agree to these terms, do not click the "I Accept" button below.</p>
          </div>
        </div>
      </Modal>;


    if (this.state.registrationDone) {
      return (
        <div className={styles.login}>
          <div className={styles.wrapper}>
            <Link to="/register" className={styles.logo}>
              <img src="/assets/images/logo.svg" width="180" alt="SashiDo" />
            </Link>
            <div className={classes.join(' ')} style={{ paddingBottom: 30 }}>
              <form action="/login" data-loading-text="Log In" className={styles.form}>
                <p className={styles.title}>Thank you! Confirmation e-mail sent.</p>
                <p>Please confirm your e-mail address to activate your account.</p>
                <br />
                <p>If you haven't received an e-mail please check your junk box or contact us.</p>
              </form>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.login}>
        <div>
          <div className={styles.showEffect} />
          <div className={styles.wrapper}>
            <Link to="/register" className={styles.logo}>
              <img src="/assets/images/logo.svg" width="180" alt="SashiDo" />
            </Link>
            <div className={classes.join(' ')}>
              <Notification note={this.state.note} fullscreen close />
              <form action="/login" data-loading-text="Log In" className={styles.form}>
                <p className={styles.title}>Get Started</p>

                <input
                  name="email"
                  placeholder="email"
                  className={styles.honeypot}
                  type="email"
                  ref={(input) => {
                    if (input) {
                      input.focus();
                      this.honeypotInput = input;
                    }
                  }} />
                {/*
                <p className={styles.subtitle}>Email</p>
              */}
                <Label text="Email" />
                <div className={styles.inputHolder}>
                  <input
                    name="username"
                    // placeholder=""
                    className={styles.username}
                    type="email"
                    disabled
                    value={emailFromTypeform}
                    ref={(input) => {
                      if (input) {
                        this.emailInput = input;
                      }
                    }} />
                  <div className={styles.inputIconHolder}>
                    <Icon name="icon-envelope" width={16} height={16} fill="#fff" />
                  </div>
                </div>


                <Label text="Your GitHub handle" />
                <div className={styles.inputHolder}>
                  <input
                    name="github"
                    className={styles.username}
                    type="text"
                    ref={(input) => {
                      if (input) {
                        this.github = input;
                      }
                    }} />
                  <div className={styles.inputIconHolder}>
                    <Icon name="icon-github" width={24} height={24} fill="#fff" />
                  </div>
                </div>


                <div className={styles.agree}>
                  <Toggle
                    value={this.state.agree}
                    onChange={agree => this.setState({ agree })} />

                  <p>I agree that SashiDo uses my e-mail for creating an account in accordance with SashiDo's<br /> <a href="javascript:;" onClick={() => this.setState({ showTos: true })}>Terms of Service</a> and <a href="javascript:;" onClick={() => this.setState({ showPolicies: true })}>Privacy Notice</a>.</p>
                </div>

                <div className={styles.agree}>
                  <Toggle
                    value={this.state.agreeMail}
                    onChange={agree => this.setState({ agreeMail: agree })} />

                  <p>I agree to receive newsletters on my e-mail in accordance with SashiDo's<br /> <a href="javascript:;" onClick={() => this.setState({ showTos: true })}>Terms of Service</a> and <a href="javascript:;" onClick={() => this.setState({ showPolicies: true })}>Privacy Notice</a>.</p>
                </div>

                <a
                  id="submit"
                  href="javascript:;"
                  className={[styles.button, (!this.state.agree) ? styles.disable : ''].join(' ')}
                  onClick={this.handleSubmitButton.bind(this)}>
                  <i className={styles.spinner} /><span>Get Started</span>
                </a>
              </form>
            </div>
            <a href="/login" className={styles.signUp}>Already have an account? Log In</a>
          </div>
          {policiesView}
          {tosView}
        </div>
      </div>
    );
  }
}
